import * as React from "react";
import { Bug, Shield, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
const services = [{
  title: "General Pest Control",
  description: <>
        Comprehensive protection against common household pests including ants, roaches, and silverfish, tailored for the Cape Coral climate. We also provide expert{" "}
        <Link to="/PortCharlottePestControlPage" className="text-primary underline">
          Port Charlotte pest control
        </Link>{" "}
        and{" "}
        <Link to="/NorthFortMyersPestControlPage" className="text-primary underline">
          North Fort Myers pest services
        </Link>.
      </>,
  icon: Zap,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/cape_coral_pest_technician.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/cape_coral_pest_technician.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/cape_coral_pest_technician.png-wvc-sizes",
    alt: "General pest control service in Cape Coral"
  }
}, {
  title: "Termite Treatment",
  description: <>
        Advanced liquid and baiting systems to protect your home from structural damage. Learn more about our{" "}
        <Link to="/TermiteTreatmentPage" className="text-primary underline">
          Cape Coral Termite Treatment
        </Link>{" "}
        options. Our team also specializes in termite protection for residents in{" "}
        <Link to="/PortCharlottePestControlPage" className="text-primary underline">
          Port Charlotte
        </Link>{" "}
        and{" "}
        <Link to="/NorthFortMyersPestControlPage" className="text-primary underline">
          North Fort Myers
        </Link>.
      </>,
  icon: Shield,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/termite_inspection_florida.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/termite_inspection_florida.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/termite_inspection_florida.png-wvc-sizes",
    alt: "Termite barrier installation in Cape Coral"
  }
}, {
  title: "Mosquito Control",
  description: <>
        Reclaim your outdoor space with our seasonal barrier sprays. View our{" "}
        <Link to="/MosquitoControlPage" className="text-primary underline">
          Cape Coral Mosquito Control
        </Link>{" "}
        plans. We are also expanding our seasonal protection to{" "}
        <Link to="/PortCharlottePestControlPage" className="text-primary underline">
          Port Charlotte
        </Link>{" "}
        and{" "}
        <Link to="/NorthFortMyersPestControlPage" className="text-primary underline">
          North Fort Myers
        </Link>{" "}
        neighborhoods.
      </>,
  icon: Bug,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/mosquito_fogging_service.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/mosquito_fogging_service.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/mosquito_fogging_service.png-wvc-sizes",
    alt: "Mosquito reduction spray in Cape Coral"
  }
}, {
  title: "Spider Control",
  description: <>
        Specialized treatments to eliminate spiders and their webs from your Cape Coral home's exterior and interior spaces. We also offer professional spider removal in{" "}
        <Link to="/PortCharlottePestControlPage" className="text-primary underline">
          Port Charlotte
        </Link>{" "}
        and{" "}
        <Link to="/NorthFortMyersPestControlPage" className="text-primary underline">
          North Fort Myers
        </Link>.
      </>,
  icon: Bug,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/spider_web_removal_service.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/spider_web_removal_service.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/spider_web_removal_service.png-wvc-sizes",
    alt: "Spider extermination service in Cape Coral"
  }
}];
export default function CapeCoralPestServices() {
  return <section id="cape-coral-pest-services" className="py-16 md:py-24 bg-background text-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold font-default mb-4 text-foreground">
            Our Pest Control Services in Cape Coral
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Comprehensive, localized pest control solutions designed specifically for the unique climate and challenges of Cape Coral residents and businesses.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {services.map((service, i) => <Card key={i} data-index={i} className="overflow-hidden flex flex-col h-full border-border hover:bg-accent/5 transition-colors duration-300">
              <div className="w-full">
                <AspectRatio ratio={16 / 9}>
                  <img src={service.image.src} data-wvc-srcset={service.image.srcset} data-wvc-sizes={service.image.sizes} alt={service.image.alt} className="object-cover w-full h-full" loading="lazy" />
                </AspectRatio>
              </div>
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-primary/10 text-primary">
                    <service.icon className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-xl font-default text-foreground">
                    {service.title}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="flex-grow">
                <CardDescription className="text-base text-muted-foreground font-sans">
                  {service.description}
                </CardDescription>
              </CardContent>
              <CardFooter className="pt-4 border-t border-border/50 mt-auto">
                <Button asChild variant="outline" className="w-full font-sans rounded-lg uppercase tracking-wider">
                  <Link to="/ContactPage">
                    Get a Quote
                  </Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>
      </div>
    </section>;
}